import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './CopyButton.css';

const ClipboardIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="5" y="3" width="8" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 5H4a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 4 15h6a1.5 1.5 0 0 0 1.5-1.5V13"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3 8.5L6.5 12L13 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function CopyButton({
  text,
  label,
  copiedLabel,
  size,
  variant,
  className,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
      } else {
        // Fallback for environments without Clipboard API
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try { document.execCommand('copy'); } catch (_) {}
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) {}
  }, [text]);

  const classNames = [
    'copy-btn',
    `copy-btn--${size}`,
    variant === 'icon' ? 'copy-btn--icon' : '',
    copied ? 'copy-btn--copied' : '',
    className || '',
  ].filter(Boolean).join(' ');

  const displayLabel = copied ? copiedLabel : label;
  const showLabel = variant !== 'icon';

  return (
    <button
      type="button"
      className={classNames}
      onClick={handleCopy}
      title={copied ? copiedLabel : `Copy: ${text}`}
      aria-label={copied ? copiedLabel : `Copy ${label || 'to clipboard'}`}
    >
      <span className="copy-btn__icon">
        {copied ? <CheckIcon /> : <ClipboardIcon />}
      </span>
      {showLabel && (
        <span className="copy-btn__label">{displayLabel}</span>
      )}
    </button>
  );
}

CopyButton.propTypes = {
  text:         PropTypes.string.isRequired,
  label:        PropTypes.string,
  copiedLabel:  PropTypes.string,
  size:         PropTypes.oneOf(['small', 'medium']),
  variant:      PropTypes.oneOf(['icon', 'button']),
  className:    PropTypes.string,
};

CopyButton.defaultProps = {
  label:       'Copy',
  copiedLabel: 'Copied!',
  size:        'small',
  variant:     'button',
};

export default CopyButton;
export { CopyButton };
